import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ─── Data ─────────────────────────────────────────────────────────────────────

const FEATURES = [
  {
    icon: '📄',
    title: 'Professional Invoicing',
    desc: 'Create beautiful invoices in seconds. Auto-numbered, line items, due dates — sent instantly.',
    color: 'bg-blue-50',
  },
  {
    icon: '📁',
    title: 'Secure File Sharing',
    desc: 'Share deliverables, briefs, and assets with clients through a secure, branded portal.',
    color: 'bg-purple-50',
  },
  {
    icon: '✍️',
    title: 'Contracts & E-Sign',
    desc: 'Send contracts with pre-built templates. Clients sign online — no printing, no scanning.',
    color: 'bg-green-50',
  },
  {
    icon: '💳',
    title: 'Stripe Payments',
    desc: 'Accept payments directly from invoices. Clients pay online with any card in seconds.',
    color: 'bg-indigo-50',
  },
  {
    icon: '🔔',
    title: 'Auto Reminders',
    desc: 'Stop chasing payments. Automated email reminders go out so you never have to ask twice.',
    color: 'bg-yellow-50',
  },
  {
    icon: '🔗',
    title: 'Client Portal',
    desc: 'Every client gets their own branded portal link to view invoices, files, and contracts.',
    color: 'bg-pink-50',
  },
];

const STEPS = [
  {
    number: '01',
    title: 'Add Your Client',
    desc: 'Create a client profile with their name, email, and company. Takes 30 seconds.',
  },
  {
    number: '02',
    title: 'Send an Invoice or Contract',
    desc: 'Generate a professional invoice or pick a contract template. Send with one click.',
  },
  {
    number: '03',
    title: 'Get Paid Online',
    desc: 'Clients pay via Stripe checkout. Invoice marks itself as paid automatically.',
  },
];

const TESTIMONIALS = [
  {
    stars: 5,
    quote: 'PortalFlow replaced three separate tools I was paying for. My clients love the portal link — they can see everything in one place without logging in.',
    name: 'Sarah Chen',
    role: 'Brand Designer',
    avatar: 'SC',
    color: 'bg-pink-500',
  },
  {
    stars: 5,
    quote: 'I used to spend hours every month chasing invoices. Now the reminders go out automatically and I get paid on time, every time.',
    name: 'Marcus Webb',
    role: 'Freelance Developer',
    avatar: 'MW',
    color: 'bg-indigo-500',
  },
  {
    stars: 5,
    quote: 'The contract templates alone saved me $200/month in legal software. Setup took 10 minutes and I was sending my first invoice within the hour.',
    name: 'Priya Nair',
    role: 'UX Consultant',
    avatar: 'PN',
    color: 'bg-emerald-500',
  },
];

const PRICING = [
  {
    name: 'Starter',
    price: '$7',
    desc: 'Perfect for freelancers just getting started.',
    features: ['Up to 5 clients', 'Professional invoicing', 'File sharing', 'Client portal', 'Email reminders'],
    cta: 'Get Started',
    highlight: false,
  },
  {
    name: 'Pro',
    price: '$15',
    desc: 'For serious freelancers ready to scale.',
    features: ['Unlimited clients', 'Everything in Starter', 'Contracts + E-signature', 'Stripe payments', 'Priority support', 'Custom branding'],
    cta: 'Start Free Trial',
    highlight: true,
    badge: 'Most Popular',
  },
];

// ─── Fake Dashboard Mockup ────────────────────────────────────────────────────

function DashboardMockup() {
  return (
    <div className="relative">
      {/* Glow */}
      <div className="absolute -inset-4 bg-indigo-500/20 rounded-3xl blur-2xl" />

      {/* Window */}
      <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Window chrome */}
        <div className="flex items-center gap-1.5 px-4 py-3 bg-gray-50 border-b border-gray-100">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
          <div className="flex-1 mx-3 h-5 bg-gray-200 rounded-md text-xs text-gray-400 flex items-center px-2 font-mono">
            localhost:3000/dashboard
          </div>
        </div>

        <div className="flex h-72">
          {/* Fake sidebar */}
          <div className="w-44 bg-gray-50 border-r border-gray-100 p-3 flex flex-col gap-1 flex-shrink-0">
            <div className="flex items-center gap-2 px-2 py-1.5 mb-2">
              <div className="w-6 h-6 bg-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-xs font-bold">P</span>
              </div>
              <span className="text-xs font-bold text-gray-800">PortalFlow</span>
            </div>
            {['Dashboard', 'Clients', 'Invoices', 'Files', 'Contracts'].map((item, i) => (
              <div key={item} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium
                ${i === 0 ? 'bg-indigo-600 text-white' : 'text-gray-500'}`}>
                <div className={`w-3 h-3 rounded-sm ${i === 0 ? 'bg-white/30' : 'bg-gray-200'}`} />
                {item}
              </div>
            ))}
          </div>

          {/* Fake content */}
          <div className="flex-1 p-4 bg-gray-50/50 overflow-hidden">
            {/* Stat cards */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {[
                { label: 'Clients', value: '12', color: 'text-indigo-600' },
                { label: 'Revenue', value: '$4,820', color: 'text-green-600' },
                { label: 'Pending', value: '$960', color: 'text-yellow-600' },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-xl p-2.5 border border-gray-100 shadow-sm">
                  <p className={`text-base font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-gray-400">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Fake invoice rows */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-50">
                <p className="text-xs font-semibold text-gray-700">Recent Invoices</p>
              </div>
              {[
                { num: 'INV-009', client: 'Acme Corp',   amount: '$1,200', status: 'Paid',    color: 'bg-green-100 text-green-700' },
                { num: 'INV-008', client: 'Studio Blue', amount: '$840',   status: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
                { num: 'INV-007', client: 'Kova Agency', amount: '$2,400', status: 'Overdue', color: 'bg-red-100 text-red-600' },
              ].map((inv) => (
                <div key={inv.num} className="flex items-center justify-between px-3 py-2 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="text-xs font-semibold text-indigo-600">{inv.num}</p>
                    <p className="text-xs text-gray-400">{inv.client}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-bold text-gray-800">{inv.amount}</p>
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${inv.color}`}>
                      {inv.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Landing() {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  // Redirect logged-in users to dashboard
  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  // Sticky navbar shadow on scroll
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">

      {/* ── Navbar ─────────────────────────────────────────────────────────── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200
        ${scrolled ? 'bg-white/95 backdrop-blur shadow-sm border-b border-gray-100' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-sm">P</span>
            </div>
            <span className="font-bold text-lg text-gray-900 tracking-tight">PortalFlow</span>
          </div>

          {/* Nav links — hidden on mobile */}
          <div className="hidden md:flex items-center gap-8">
            {[['Features', 'features'], ['Pricing', 'pricing'], ['How It Works', 'how']].map(([label, id]) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="text-sm font-medium text-gray-600 hover:text-indigo-600 transition"
              >
                {label}
              </button>
            ))}
          </div>

          {/* CTA buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/login')}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 transition px-3 py-1.5"
            >
              Login
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white
                px-4 py-2 rounded-xl transition shadow-sm"
            >
              Start Free Trial
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-purple-50" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-100 rounded-full blur-3xl opacity-40 translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-100 rounded-full blur-3xl opacity-30 -translate-x-1/4" />

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* Left — text */}
            <div>
              <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-100
                text-indigo-700 text-xs font-semibold px-3.5 py-1.5 rounded-full mb-6">
                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-pulse" />
                Built for freelancers & agencies
              </div>

              <h1 className="text-5xl sm:text-6xl font-extrabold text-gray-900 leading-tight tracking-tight mb-6">
                The Client Portal
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-600">
                  Built for Freelancers
                </span>
              </h1>

              <p className="text-lg text-gray-500 leading-relaxed mb-8 max-w-lg">
                Send invoices, share files, sign contracts, and get paid — all in one place.
                Starting at just <span className="font-semibold text-gray-700">$7/month</span>.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate('/signup')}
                  className="inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700
                    text-white font-semibold px-7 py-3.5 rounded-xl transition shadow-lg
                    shadow-indigo-200 text-sm"
                >
                  Start for Free →
                </button>
                <button
                  onClick={() => scrollTo('how')}
                  className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50
                    text-gray-700 font-semibold px-7 py-3.5 rounded-xl transition border border-gray-200
                    shadow-sm text-sm"
                >
                  See How It Works
                </button>
              </div>

              {/* Social proof */}
              <div className="flex items-center gap-3 mt-8">
                <div className="flex -space-x-2">
                  {['bg-indigo-500', 'bg-pink-500', 'bg-emerald-500', 'bg-yellow-500'].map((c, i) => (
                    <div key={i} className={`w-8 h-8 ${c} rounded-full border-2 border-white flex items-center justify-center`}>
                      <span className="text-white text-xs font-bold">{['S','M','P','A'][i]}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-500">
                  <span className="font-semibold text-gray-700">500+</span> freelancers already using PortalFlow
                </p>
              </div>
            </div>

            {/* Right — mockup */}
            <div className="hidden lg:block">
              <DashboardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ───────────────────────────────────────────────────────── */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-indigo-600 font-semibold text-sm uppercase tracking-wider mb-3">Everything you need</p>
            <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight">
              Run your freelance business
              <br />from one dashboard
            </h2>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto">
              Stop juggling five different tools. PortalFlow gives you everything in one clean, simple app.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="group bg-white border border-gray-100 rounded-2xl p-6 shadow-sm
                  hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-default"
              >
                <div className={`w-12 h-12 ${f.color} rounded-2xl flex items-center justify-center mb-4 text-2xl`}>
                  {f.icon}
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ───────────────────────────────────────────────────── */}
      <section id="how" className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-indigo-600 font-semibold text-sm uppercase tracking-wider mb-3">Simple by design</p>
            <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight">Up and running in minutes</h2>
            <p className="text-gray-500 mt-4">No complicated setup. No learning curve. Just add a client and go.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-10 left-1/4 right-1/4 h-0.5 bg-indigo-100 z-0" />

            {STEPS.map((step, i) => (
              <div key={step.number} className="relative text-center">
                <div className="w-20 h-20 bg-white border-2 border-indigo-100 rounded-2xl flex flex-col
                  items-center justify-center mx-auto mb-5 shadow-sm relative z-10">
                  <span className="text-xs font-bold text-indigo-400 uppercase tracking-widest">Step</span>
                  <span className="text-2xl font-extrabold text-indigo-600">{i + 1}</span>
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{step.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed max-w-xs mx-auto">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ────────────────────────────────────────────────────────── */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-indigo-600 font-semibold text-sm uppercase tracking-wider mb-3">Simple pricing</p>
            <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight">
              No hidden fees. Cancel anytime.
            </h2>
            <p className="text-gray-500 mt-4">Start free, upgrade when you're ready.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            {PRICING.map((plan) => (
              <div
                key={plan.name}
                className={`relative rounded-2xl p-8 border transition-all
                  ${plan.highlight
                    ? 'bg-indigo-600 border-indigo-500 shadow-2xl shadow-indigo-200 scale-105'
                    : 'bg-white border-gray-200 shadow-sm hover:shadow-md'
                  }`}
              >
                {plan.badge && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-yellow-400
                    to-orange-400 text-white text-xs font-bold px-4 py-1 rounded-full shadow">
                    {plan.badge}
                  </span>
                )}

                <div className="mb-6">
                  <h3 className={`text-lg font-bold mb-1 ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>
                    {plan.name}
                  </h3>
                  <p className={`text-sm mb-4 ${plan.highlight ? 'text-indigo-200' : 'text-gray-500'}`}>
                    {plan.desc}
                  </p>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-5xl font-extrabold tracking-tight ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>
                      {plan.price}
                    </span>
                    <span className={`text-sm font-medium ${plan.highlight ? 'text-indigo-200' : 'text-gray-400'}`}>/month</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2.5">
                      <svg className={`w-4 h-4 flex-shrink-0 ${plan.highlight ? 'text-indigo-200' : 'text-indigo-500'}`}
                        fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" clipRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                      </svg>
                      <span className={`text-sm ${plan.highlight ? 'text-indigo-100' : 'text-gray-600'}`}>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate('/signup')}
                  className={`w-full py-3 rounded-xl font-semibold text-sm transition
                    ${plan.highlight
                      ? 'bg-white text-indigo-600 hover:bg-indigo-50 shadow-sm'
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm'
                    }`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ───────────────────────────────────────────────────── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-indigo-600 font-semibold text-sm uppercase tracking-wider mb-3">Loved by freelancers</p>
            <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight">
              Don't take our word for it
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name}
                className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all">
                {/* Stars */}
                <div className="flex gap-0.5 mb-4">
                  {[...Array(t.stars)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371
                        1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54
                        1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1
                        1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>

                <p className="text-gray-600 text-sm leading-relaxed mb-5 italic">"{t.quote}"</p>

                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 ${t.color} rounded-full flex items-center justify-center flex-shrink-0`}>
                    <span className="text-white text-xs font-bold">{t.avatar}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{t.name}</p>
                    <p className="text-xs text-gray-400">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────────────────────── */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-violet-700" />
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-white/5 rounded-full blur-3xl" />

        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 tracking-tight">
            Ready to get started?
          </h2>
          <p className="text-indigo-200 text-lg mb-10">
            Join 500+ freelancers who use PortalFlow to run their business. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/signup')}
              className="bg-white hover:bg-gray-50 text-indigo-700 font-bold px-8 py-4
                rounded-xl transition shadow-xl text-sm"
            >
              Start for Free — No Credit Card
            </button>
            <button
              onClick={() => navigate('/login')}
              className="bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-4
                rounded-xl transition border border-white/20 text-sm"
            >
              Sign In
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <footer className="bg-gray-900 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Logo */}
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">P</span>
              </div>
              <span className="font-bold text-white text-lg tracking-tight">PortalFlow</span>
            </div>

            {/* Links */}
            <div className="flex items-center gap-8">
              {[['Features', 'features'], ['Pricing', 'pricing'], ['How It Works', 'how']].map(([label, id]) => (
                <button
                  key={id}
                  onClick={() => scrollTo(id)}
                  className="text-sm text-gray-400 hover:text-white transition font-medium"
                >
                  {label}
                </button>
              ))}
              <button
                onClick={() => navigate('/login')}
                className="text-sm text-gray-400 hover:text-white transition font-medium"
              >
                Login
              </button>
            </div>

            <p className="text-sm text-gray-500">© 2026 PortalFlow. All rights reserved.</p>
          </div>
        </div>
      </footer>

    </div>
  );
}
